/**
 * THREAT / CRIME HEATMAP DRAPE — roadmap item #1 (Brockmann-approved 2026-06-23).
 *
 * "imagery-layer drape on the 3D tileset, the SAME transparent mechanism
 *  shipped for live traffic."
 *
 * So this deliberately mirrors GoogleTrafficLayer.tsx:
 *   - adds to `tileset.imageryLayers`, NEVER `viewer.imageryLayers` — the
 *     default globe is hidden (scene.globe.show=false), so an ellipsoid layer
 *     renders nothing and Cesium never even requests it,
 *   - reads the tileset off `window.__tileset` with the same retry-until-ready
 *     loop, because the tileset loads asynchronously after first paint.
 *
 * Data comes from /threat-heatmap, which aggregates GOVERNMENT-PUBLISHED OPEN
 * DATA only (Houston PD NIBRS + GDACS). Where no public source covers the
 * viewport the response is empty and this layer paints nothing — it never
 * invents incidents.
 *
 * DEFAULT OFF. It changes what the shipped globe looks like, so an operator
 * opts in.
 */

import { createEffect, onCleanup } from "solid-js";
import { useCesium } from "../../cesium/useCesium.ts";
import { groundState, setThreatMeta } from "./store.ts";
import { PROXY_BASE_URL } from "../../config.ts";
import { groundRectDegrees } from "../../utils/viewRect.ts";
/** Heat field raster size. Power of two keeps Cesium's sampler happy. */
const TEX = 1024;

/** Influence radius of a single incident, in metres on the ground. */
const POINT_RADIUS_M = 260;

/** Wait for camera to settle before re-querying — the droplet is 2 vCPU. */
const DEBOUNCE_MS = 900;

/** Above this the view is regional, not tactical; stop drawing. */
const MAX_ALTITUDE_M = 260_000;

/** Must match the server's too-wide guard in routes/threat.ts. */
const MAX_SPAN_DEG = 12;
/**
 * Paint the weighted points into an alpha field, then colourise.
 * Two-pass (accumulate grey -> map to ramp) is what makes overlapping
 * incidents read as a hot zone instead of a pile of discs.
 */
function renderHeatCanvas(points, west, south, east, north) {
  const canvas = document.createElement("canvas");
  canvas.width = TEX;
  canvas.height = TEX;
  const ctx = canvas.getContext("2d", {
    willReadFrequently: true
  });
  ctx.clearRect(0, 0, TEX, TEX);
  const lonSpan = east - west;
  const latSpan = north - south;
  if (lonSpan <= 0 || latSpan <= 0) return canvas;

  // Convert the metre radius into texels. 1 deg lat ~ 111.32 km everywhere.
  const degPerMetreLat = 1 / 111_320;
  const radiusDeg = POINT_RADIUS_M * degPerMetreLat;
  const radiusPx = Math.max(6, Math.min(110, radiusDeg / latSpan * TEX));

  // ---- pass 1: accumulate intensity as greyscale alpha ----
  ctx.globalCompositeOperation = "lighter";
  for (const p of points) {
    const x = (p.lon - west) / lonSpan * TEX;
    const y = (north - p.lat) / latSpan * TEX; // north at top
    if (x < -radiusPx || x > TEX + radiusPx || y < -radiusPx || y > TEX + radiusPx) continue;
    const grad = ctx.createRadialGradient(x, y, 0, x, y, radiusPx);
    const a = Math.max(0.05, Math.min(1, p.w)) * 0.55;
    grad.addColorStop(0, `rgba(255,255,255,${a})`);
    grad.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(x, y, radiusPx, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalCompositeOperation = "source-over";

  // ---- pass 2: map accumulated alpha through a threat ramp ----
  const img = ctx.getImageData(0, 0, TEX, TEX);
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    const t = d[i + 3] / 255; // accumulated intensity 0..1
    if (t <= 0.02) {
      d[i + 3] = 0;
      continue;
    }
    let r, g, b;
    if (t < 0.33) {
      // cool -> teal (low incident density)
      const k = t / 0.33;
      r = 0;
      g = Math.round(150 + 70 * k);
      b = Math.round(255 - 60 * k);
    } else if (t < 0.66) {
      // teal -> amber
      const k = (t - 0.33) / 0.33;
      r = Math.round(255 * k);
      g = Math.round(220 - 30 * k);
      b = Math.round(195 - 180 * k);
    } else {
      // amber -> red (hot zone)
      const k = Math.min(1, (t - 0.66) / 0.34);
      r = 255;
      g = Math.round(190 - 150 * k);
      b = Math.round(15 + 10 * k);
    }
    d[i] = r;
    d[i + 1] = g;
    d[i + 2] = b;
    // Cap opacity so the photorealistic tileset stays readable underneath —
    // same principle as the transparent traffic drape.
    d[i + 3] = Math.round(Math.min(0.68, t * 1.15) * 255);
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
}
export function ThreatHeatmapLayer() {
  const {
    viewer,
    ready
  } = useCesium();
  let layer = null;
  let timer = null;
  let moveEndHandler = null;
  let tries = 0;
  let generation = 0; // guards against a slow fetch landing after toggle-off

  const getTileset = () => window.__tileset;
  const remove = () => {
    const ts = getTileset();
    if (ts?.imageryLayers && layer) {
      try {
        ts.imageryLayers.remove(layer, true);
      } catch (_) {
        /* tileset may already be torn down */
      }
    }
    layer = null;
  };

  /**
   * Ground footprint of the current view.
   * Uses pickEllipsoid, NOT camera.computeViewRectangle() — on this
   * hidden-globe scene the latter returns the WHOLE GLOBE (measured
   * 2026-08-17: span 180x360 from 2500 m over Houston), which would pin this
   * layer at "zoom in for threat data" permanently.
   */
  const viewRectDegrees = () => {
    const v = viewer();
    if (!v || v.isDestroyed()) return null;
    return groundRectDegrees(v, MAX_SPAN_DEG);
  };
  const refresh = async () => {
    if (!groundState.threatHeatmapEnabled) return;
    const v = viewer();
    if (!v || v.isDestroyed()) return;
    const ts = getTileset();
    if (!ts?.imageryLayers) {
      // 3D tileset not ready yet — same retry cadence as the traffic layer.
      if (tries++ < 30) {
        setTimeout(() => {
          if (groundState.threatHeatmapEnabled) refresh();
        }, 700);
      }
      return;
    }
    const carto = v.camera.positionCartographic;
    if (carto && carto.height > MAX_ALTITUDE_M) {
      remove();
      setThreatMeta({
        status: "zoom in for threat data",
        sources: [],
        count: 0
      });
      return;
    }
    const rect = viewRectDegrees();
    if (!rect) return;
    const gen = ++generation;
    setThreatMeta({
      status: "loading…",
      sources: [],
      count: 0
    });
    try {
      const url = `${PROXY_BASE_URL}/api/threat-heatmap` + `?minLat=${rect.s.toFixed(4)}&maxLat=${rect.n.toFixed(4)}` + `&minLon=${rect.w.toFixed(4)}&maxLon=${rect.e.toFixed(4)}`;
      const res = await fetch(url);
      const data = await res.json();

      // Toggled off, or superseded by a newer camera position, while in flight.
      if (gen !== generation || !groundState.threatHeatmapEnabled) return;
      if (data?.tooWide) {
        remove();
        setThreatMeta({
          status: "zoom in for threat data",
          sources: [],
          count: 0
        });
        return;
      }
      const points = Array.isArray(data?.points) ? data.points : [];
      if (!points.length) {
        remove();
        setThreatMeta({
          status: data?.failed?.length ? "source unavailable" : "no public data here",
          sources: [],
          count: 0
        });
        return;
      }
      const canvas = renderHeatCanvas(points, rect.w, rect.s, rect.e, rect.n);
      const before = ts.imageryLayers.length;
      remove();
      const provider = new Cesium.SingleTileImageryProvider({
        url: canvas.toDataURL("image/png"),
        rectangle: Cesium.Rectangle.fromDegrees(rect.w, rect.s, rect.e, rect.n),
        tileWidth: TEX,
        tileHeight: TEX,
        credit: "Public open data · HPD NIBRS / GDACS"
      });
      const l = new Cesium.ImageryLayer(provider);
      l.alpha = 1.0;
      ts.imageryLayers.add(l);
      layer = l;

      // PROVE THE WIRE: assert the layer actually landed on the tileset.
      // "no exception thrown" is not evidence — a failed add would be silent.
      const after = ts.imageryLayers.length;
      const landed = ts.imageryLayers.contains(l);
      console.log(`[ThreatHeatmap] points=${points.length} imageryLayers ${before}->${after} contains=${landed} sources=${(data.sources || []).join(" + ") || "none"}`);
      setThreatMeta({
        status: landed ? `${points.length} incidents` : "layer add FAILED",
        sources: data.sources || [],
        count: points.length
      });
      v.scene.requestRender();
    } catch (e) {
      console.error("[ThreatHeatmap] failed:", e);
      if (gen === generation) {
        setThreatMeta({
          status: "fetch failed",
          sources: [],
          count: 0
        });
      }
    }
  };
  const scheduleRefresh = () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(refresh, DEBOUNCE_MS);
  };
  createEffect(() => {
    const on = groundState.threatHeatmapEnabled;
    if (!ready()) return;
    const v = viewer();
    if (!v || v.isDestroyed()) return;

    // Always detach first so toggling never stacks listeners.
    if (moveEndHandler) {
      v.camera.moveEnd.removeEventListener(moveEndHandler);
      moveEndHandler = null;
    }
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    if (on) {
      tries = 0;
      moveEndHandler = scheduleRefresh;
      v.camera.moveEnd.addEventListener(moveEndHandler);
      refresh();
    } else {
      generation++; // invalidate any in-flight fetch
      remove();
      setThreatMeta({
        status: "off",
        sources: [],
        count: 0
      });
      v.scene.requestRender();
    }
  });
  onCleanup(() => {
    generation++;
    const v = viewer();
    if (v && !v.isDestroyed() && moveEndHandler) {
      v.camera.moveEnd.removeEventListener(moveEndHandler);
    }
    if (timer) clearTimeout(timer);
    remove();
  });
  return null;
}
export default ThreatHeatmapLayer;